import React from "react";
import { ScrollText, Flame, BookOpen, HandHelping } from "lucide-react";

const PARA_UTILISATION = [
  "En raison du caractère très ancien de la plupart des prières, elles sont souvent adressées à Dieu, Jésus, Marie ou des Saints.",
  "La plupart des prières ne sont utilisées que pour une seule maladie mais certaines ont ce pouvoir de soulager plusieurs maux.",
  "On parle souvent de « don ». Je demeure assez sceptique sur le bien-fondé de ce mot car cela voudrait dire que certains d'entre nous ont reçu le don de soulager son prochain et d'autres n'en ont pas le droit.",
  "Aussi, à mon humble avis, le don vient bien plus de connaître les formules et les utiliser que de s'autoproclamer « porteur d'un don ».",
];

const PARA_UTILISATION_2 = [
  "La pratique se fait en face de la personne à soulager mais certains pratiquent par téléphone ou par photo. Il semblerait même que certains hôpitaux aient déjà fait appel à des coupeurs de feu.",
  "Je ne pratique pas à distance car je suis mal à l'aise avec le principe mais cela ne veut pas dire que ça ne fonctionne pas. À vous de tester.",
];

const RAPPEL = "Les prières et rituels présentés sur ce site ne constituent ni un avis médical ni une alternative à un suivi professionnel. Pour toute question concernant votre santé physique ou mentale, consultez un professionnel de santé.";

const PARA_PAYER = [
  "Autre chose importante : que ce soit pour soulager des maux de dents, de tête, des brûlures ou quoi que ce soit d'autre, je vous conseille vivement de ne pas vous faire payer. L'utilisation des prières se fait pour aider son prochain et non pour gagner de l'argent.",
  "Certains « panseurs » font payer leur prestation mais c'est souvent dû au fait qu'ils proposent d'autres prestations que de simples soulagements. Pour ceux qui font payer une simple coupure de feu, que bien leur en fasse…",
  "En revanche, vous pouvez accepter des dons (argent, nourriture, un service…), mais sans demander. Uniquement si on vous le propose.",
  "Néanmoins, une chose est vraie : le fait de payer encourage le patient à s'investir dans la démarche et donc à accélérer son propre soulagement.",
  "Je vous laisse donc maitre de choisir de faire payer ou pas. Mais je vous encourage très vivement à n'accepter que des dons car, que l'on vous paye ou pas, tout le monde a le droit à votre aide. À vous de voir…",
];

const SITE_PAYANT_TITRE = "Pourquoi ce site est-il payant ?";
const SITE_PAYANT_1 = "Pour déverrouiller l'ensemble du site, un abonnement est nécessaire car certaines prières sont complexes et très difficiles à obtenir (notamment les exorcismes). Nous demandons une contribution qui nous permet de continuer à chercher, à fouiller des archives ou à se procurer des textes interdits ou protégés.";

const IMPERATIFS = [
  "Le principal est l'intention que vous mettez dans votre pratique. Vous utilisez une prière avec la réelle envie d'aider et de soulager.",
  "Vous devez être convaincu que ça ne peut que marcher.",
  "Vous avez une confiance totale et absolue dans la prière que vous avez choisie (même si vous n'êtes pas pratiquant).",
  "Apprenez les prières par cœur pour éviter de « bloquer » lors de leur utilisation ; ça risquerait de vous déconcentrer et de vous faire perdre confiance.",
  "Fixez l'endroit de la douleur en récitant la prière avec l'intention de la soulager.",
  "Si la prière est trop longue, lisez-la, mais ce n'est pas l'idéal pour pouvoir garder les yeux sur l'endroit à soulager.",
];

const Pratique = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20" data-testid="pratique-page">
      <p className="font-engraved text-[var(--gold)] text-[11px] mb-4">Conseils du panseur</p>
      <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--ivory)] leading-tight">
        <em className="text-[var(--gold)]">Pratique</em> et conseils
      </h1>

      <div className="divider-ornament my-12"><Flame size={14} strokeWidth={1.2} /></div>

      <section className="mb-16">
        <div className="flex items-center gap-3 mb-6">
          <BookOpen className="text-[var(--gold)]" strokeWidth={1.1} size={24} />
          <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Utilisation des prières</h2>
        </div>
        <div className="font-serif-body text-[var(--ivory-muted)] text-lg leading-loose space-y-5">
          {PARA_UTILISATION.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          <div className="sacred-card sharp p-5 border-l-4 border-l-[var(--gold)] my-6">
            <p className="text-sm">
              <span className="font-engraved text-[var(--gold)] text-[10px] mr-2">Rappel :</span>
              {RAPPEL}
            </p>
          </div>
          {PARA_UTILISATION_2.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
        </div>
      </section>

      <div className="divider-ornament my-12"><ScrollText size={14} strokeWidth={1.2} /></div>

      <section className="mb-16">
        <div className="flex items-center gap-3 mb-6">
          <HandHelping className="text-[var(--gold)]" strokeWidth={1.1} size={24} />
          <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Se faire payer ?</h2>
        </div>
        <div className="font-serif-body text-[var(--ivory-muted)] text-lg leading-loose space-y-5">
          {PARA_PAYER.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          <div className="sacred-card sharp p-6 mt-8">
            <p className="font-engraved text-[var(--gold)] text-[10px] mb-3">{SITE_PAYANT_TITRE}</p>
            <p className="text-base">{SITE_PAYANT_1}</p>
          </div>
        </div>
      </section>

      <div className="divider-ornament my-12"><Flame size={14} strokeWidth={1.2} /></div>

      <section>
        <div className="flex items-center gap-3 mb-6">
          <ScrollText className="text-[var(--gold)]" strokeWidth={1.1} size={24} />
          <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Comment utiliser les prières</h2>
        </div>
        <p className="font-serif-body text-[var(--ivory-muted)] text-lg leading-loose mb-8">
          {"Il n'y a pas de mode d'emploi mais juste des conseils importants :"}
        </p>

        <ul className="space-y-5">
          {IMPERATIFS.map((txt, i) => (
            <li key={txt.slice(0, 40)} className="flex gap-5 items-start" data-testid={`imperatif-${i}`}>
              <span className="font-serif-display text-3xl text-[var(--gold)] leading-none mt-1">{i + 1}</span>
              <p className="font-serif-body text-[var(--ivory)] text-lg leading-relaxed flex-1">{txt}</p>
            </li>
          ))}
          <li className="flex gap-5 items-start" data-testid="imperatif-6">
            <span className="font-serif-display text-3xl text-[var(--gold)] leading-none mt-1">7</span>
            <p className="font-serif-body text-[var(--ivory)] text-lg leading-relaxed flex-1">
              {"Vous devrez utiliser des signes de croix (à chaque fois que vous rencontrerez le signe "}
              <span className="text-[var(--gold)]">†</span>
              {") même si vous n'êtes pas croyant : faites-le avec conviction."}
            </p>
          </li>
          <li className="flex gap-5 items-start" data-testid="imperatif-7">
            <span className="font-serif-display text-3xl text-[var(--gold)] leading-none mt-1">8</span>
            <p className="font-serif-body text-[var(--ivory)] text-lg leading-relaxed flex-1">
              {"Nous vous encourageons à faire très attention à la pratique des prières d'exorcisme (particulièrement "}
              <em className="text-[var(--gold)]">De Exorcizandis Obsessis a Demonio</em>
              {") et à les utiliser uniquement si vous êtes formés et croyants."}
            </p>
          </li>
        </ul>

        <p className="font-serif-display italic text-2xl text-[var(--gold)] text-center mt-16">
          {"Voilà ! C'est à vous !"}
        </p>
        <div className="divider-ornament mt-10"><Flame size={14} strokeWidth={1.2} /></div>
      </section>
    </div>
  );
};

export default Pratique;
